// In src/pages/Register.js

import React, { useState, useEffect } from 'react';
import { useNavigate, Link as RouterLink, useSearchParams } from 'react-router-dom';
import { signUp } from '../services/auth';
import { db } from '../services/firebase';
// MODIFIED: Added orderBy to the Firestore imports, and doc, setDoc for subcollection
import { collection, doc, setDoc, serverTimestamp, query, where, getDocs, orderBy } from 'firebase/firestore'; 

// Import Material-UI components
import { Avatar, Button, TextField, Link, Grid, Box, Typography, Container, CircularProgress } from '@mui/material'; 
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import Autocomplete from '@mui/material/Autocomplete';

function Register() {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState(''); // This email is for auth, but also saved to private info
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const [searchParams] = useSearchParams();
  const [inviteChurchId, setInviteChurchId] = useState(null);

  useEffect(() => {
    const churchIdFromUrl = searchParams.get('churchId');
    if (churchIdFromUrl) {
      setInviteChurchId(churchIdFromUrl);
    }
  }, [searchParams]);

  const [churchesList, setChurchesList] = useState([]);
  const [selectedChurch, setSelectedChurch] = useState(null);
  const [churchSearchInput, setChurchSearchInput] = useState('');
  const [fetchingChurches, setFetchingChurches] = useState(true);

  useEffect(() => {
    const fetchChurches = async () => {
      setFetchingChurches(true);
      try {
        // MODIFIED: Added orderBy('name', 'asc') to sort churches alphabetically by name
        const q = query(
          collection(db, 'churches'), 
          where('isPubliclyDiscoverable', '==', true),
          orderBy('name', 'asc') // <--- ADDED THIS LINE
        );
        const querySnapshot = await getDocs(q);
        setChurchesList(querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
      } catch (err) {
        console.error("Error fetching churches for registration:", err);
        setError("Failed to load church list. Please try again later.");
      } finally {
        setFetchingChurches(false);
      }
    };

    if (!inviteChurchId) {
      fetchChurches();
    } else {
      setFetchingChurches(false);
    }
  }, [inviteChurchId]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (password !== confirmPassword) {
      return setError('Passwords do not match');
    }

    setLoading(true);

    try {
      const userCredential = await signUp(email, password); // Firebase Auth handles email for auth
      const user = userCredential.user;

      let userStatus = 'pending_approval';
      let assignedChurchId = null;
      let shouldSetClaimsImmediately = false;

      if (inviteChurchId) {
        userStatus = 'active';
        assignedChurchId = inviteChurchId;
        shouldSetClaimsImmediately = true;
      } else if (selectedChurch) {
        assignedChurchId = selectedChurch.id;
        if (selectedChurch.autoJoinEnabled === true) {
          userStatus = 'active';
          shouldSetClaimsImmediately = true;
        } else {
          userStatus = 'pending_approval';
          shouldSetClaimsImmediately = false;
        }
      } else {
        userStatus = 'pending_approval';
        assignedChurchId = null;
        shouldSetClaimsImmediately = false;
      }

      const userDocRef = doc(db, 'users', user.uid);
      const privateContactInfoDocRef = doc(db, 'users', user.uid, 'privateContactInfo', 'details'); // NEW: Private info doc ref

      // Save non-sensitive user data to the main user document
      await setDoc(userDocRef, {
        authUid: user.uid,
        firstName,
        lastName,
        profilePhotoUrl: null, // NEW: Initialize profilePhotoUrl as null
        role: 'Member',
        status: userStatus,
        churchId: assignedChurchId,
        createdAt: serverTimestamp(),
        // New permission fields default to false
        canSendNotifications: false,
        canViewPrivatePrayers: false,
        canEditHomePage: false,
      });

      // NEW: Save sensitive contact info (including email) to the private subcollection
      await setDoc(privateContactInfoDocRef, {
        email: email, // Save email here
        phone: '', // Initialize other sensitive fields as empty
        address: '',
        city: '',
        state: '',
        zip: '',
        birthday: null,
      });


      if (shouldSetClaimsImmediately) {
        const idToken = await user.getIdToken();
        await fetch('https://setcustomclaims-ltocsivqja-uc.a.run.app', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ idToken }),
        });
        await user.getIdToken(true);

        alert('Registration successful! Welcome to the community.');
        navigate('/');
      } else {
        alert(`Registration successful! Your request to join ${selectedChurch?.name || 'a church'} is pending approval.`);
        navigate('/login');
      }

    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container component="main" maxWidth="xs">
      <Box sx={{ marginTop: 8, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <Avatar sx={{ m: 1, bgcolor: 'secondary.main' }}>
          <LockOutlinedIcon />
        </Avatar>
        <Typography component="h1" variant="h5">
          Sign Up
        </Typography>
        {inviteChurchId && (
          <Typography variant="subtitle1" color="primary" sx={{ mt: 1 }}>
            You are joining a specific church community!
          </Typography>
        )}
        <Box component="form" noValidate onSubmit={handleSubmit} sx={{ mt: 3 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField name="firstName" required fullWidth id="firstName" label="First Name" autoFocus value={firstName} onChange={(e) => setFirstName(e.target.value)} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField required fullWidth id="lastName" label="Last Name" name="lastName" value={lastName} onChange={(e) => setLastName(e.target.value)} />
            </Grid>
            <Grid item xs={12}>
              <TextField required fullWidth id="email" label="Email Address" name="email" value={email} onChange={(e) => setEmail(e.target.value)} />
            </Grid>
            <Grid item xs={12}>
              <TextField required fullWidth name="password" label="Password" type="password" id="password" value={password} onChange={(e) => setPassword(e.target.value)} />
            </Grid>
            <Grid item xs={12}>
              <TextField required fullWidth name="confirmPassword" label="Confirm Password" type="password" id="confirmPassword" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} />
            </Grid>
          </Grid>

          {/* NEW: Church Search/Selection UI */}
          {!inviteChurchId && ( // Only show if not coming from an invite link
            <>
              <Typography variant="body1" sx={{ mt: 3, mb: 1 }}>
                Join a church:
              </Typography>
              {fetchingChurches ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
                  <CircularProgress size={24} />
                </Box>
              ) : (
                <Autocomplete
                  options={churchesList}
                  // MODIFIED: getOptionLabel to display church name, city, and state
                  getOptionLabel={(option) => `${option.name || ''} (${option.city || 'N/A'}, ${option.state || 'N/A'})`}
                  onChange={(event, newValue) => {
                    setSelectedChurch(newValue);
                  }}
                  inputValue={churchSearchInput}
                  onInputChange={(event, newInputValue) => {
                    setChurchSearchInput(newInputValue);
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Search for your Church (Optional)"
                      margin="normal"
                      fullWidth
                    />
                  )}
                  sx={{ mt: 1, mb: 1 }}
                />
              )}
              {selectedChurch && (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Selected: {selectedChurch.name} ({selectedChurch.city}, {selectedChurch.state})
                </Typography>
              )}
            </>
          )}

          {error && <Typography color="error" variant="body2" sx={{ mt: 2 }}>{error}</Typography>}
          <Button type="submit" fullWidth variant="contained" disabled={loading} sx={{ mt: 3, mb: 2 }}>
            {loading ? 'Signing Up...' : 'Sign Up'}
          </Button>
          <Grid container justifyContent="flex-end">
            <Grid item>
              <Link component={RouterLink} to="/login" variant="body2">
                Already have an account? Sign in
              </Link>
            </Grid>
          </Grid>
        </Box>
      </Box>
    </Container>
  );
}

export default Register;